<template>
  <div class="library__view">
    <h1 class="library__title">Sentry Integration Test</h1>
    <p class="text--muted mb-4">
      Validate that Sentry error reporting is working correctly. Each button
      triggers a different type of error.
    </p>

    <div v-if="!sentry_enabled" class="mb-4">
      <b-alert show variant="warning">
        <strong>Sentry is disabled.</strong>
        Make sure the DSN is set in the correct in the <code>.env</code> file.
        Errors will still be thrown but won't reach Sentry.
      </b-alert>
    </div>

    <div v-else class="mb-4">
      <b-alert show variant="success">
        <strong>Sentry is enabled.</strong>
        DSN: <code>{{ dsn_masked }}</code> &mdash; Environment:
        <code>{{ app_mode }}</code>
      </b-alert>
    </div>

    <!-- section: JS Errors -->
    <section id="js-errors" class="library__section">
      <p class="library__section-title">JavaScript Errors</p>
      <p class="text--muted mb-3">
        Basic JS errors that should be captured by Sentry's global error
        handler.
      </p>

      <div class="sentry-test-grid">
        <div
          v-for="test_case in js_error_cases"
          :key="test_case.id"
          class="sentry-test-card"
        >
          <div class="sentry-test-card__header">
            <span
              class="sentry-test-card__badge"
              :class="`sentry-test-card__badge--${test_case.severity}`"
            >
              {{ test_case.severity }}
            </span>
            <span class="sentry-test-card__type">{{ test_case.type }}</span>
          </div>
          <p class="sentry-test-card__title">{{ test_case.title }}</p>
          <p class="sentry-test-card__description">
            {{ test_case.description }}
          </p>
          <div class="sentry-test-card__footer">
            <span class="sentry-test-card__expected">
              Sentry:
              <strong>{{
                test_case.should_report ? "YES" : "FILTERED"
              }}</strong>
            </span>
            <vb-button
              @click.native="run_test(test_case)"
              :vb_options="{
                text: test_case.running ? 'Running...' : 'Trigger',
                variant: test_case.should_report ? 'danger' : 'secondary',
                size: 'sm',
                disabled: test_case.running,
              }"
            />
          </div>
          <div
            v-if="test_case.result"
            class="sentry-test-card__result"
            :class="`sentry-test-card__result--${test_case.result.status}`"
          >
            {{ test_case.result.message }}
          </div>
        </div>
      </div>
    </section>

    <!-- section: Sentry API (captureException / captureMessage) -->
    <section id="sentry-api" class="library__section">
      <p class="library__section-title">
        Sentry API (captureException / captureMessage)
      </p>
      <p class="text--muted mb-3">
        Direct Sentry SDK calls to test manual error reporting and message
        capture.
      </p>

      <div class="sentry-test-grid">
        <div
          v-for="test_case in sentry_api_cases"
          :key="test_case.id"
          class="sentry-test-card"
        >
          <div class="sentry-test-card__header">
            <span
              class="sentry-test-card__badge"
              :class="`sentry-test-card__badge--${test_case.severity}`"
            >
              {{ test_case.severity }}
            </span>
            <span class="sentry-test-card__type">{{ test_case.type }}</span>
          </div>
          <p class="sentry-test-card__title">{{ test_case.title }}</p>
          <p class="sentry-test-card__description">
            {{ test_case.description }}
          </p>
          <div class="sentry-test-card__footer">
            <span class="sentry-test-card__expected">
              Sentry:
              <strong>{{
                test_case.should_report ? "YES" : "FILTERED"
              }}</strong>
            </span>
            <vb-button
              @click.native="run_test(test_case)"
              :vb_options="{
                text: test_case.running ? 'Running...' : 'Trigger',
                variant: test_case.should_report ? 'danger' : 'secondary',
                size: 'sm',
                disabled: test_case.running,
              }"
            />
          </div>
          <div
            v-if="test_case.result"
            class="sentry-test-card__result"
            :class="`sentry-test-card__result--${test_case.result.status}`"
          >
            {{ test_case.result.message }}
          </div>
        </div>
      </div>
    </section>

    <!-- section: Noise Filtering (should NOT reach Sentry) -->
    <section id="noise-filtering" class="library__section">
      <p class="library__section-title">
        Noise Filtering (should NOT reach Sentry)
      </p>
      <p class="text--muted mb-3">
        These errors should be filtered out by <code>ignoreErrors</code>,
        <code>beforeSend</code>, or <code>should_report_to_sentry()</code>. If
        they appear in Sentry, the filtering config needs fixing.
      </p>

      <div class="sentry-test-grid">
        <div
          v-for="test_case in noise_filtering_cases"
          :key="test_case.id"
          class="sentry-test-card"
        >
          <div class="sentry-test-card__header">
            <span
              class="sentry-test-card__badge sentry-test-card__badge--filtered"
            >
              filtered
            </span>
            <span class="sentry-test-card__type">{{ test_case.type }}</span>
          </div>
          <p class="sentry-test-card__title">{{ test_case.title }}</p>
          <p class="sentry-test-card__description">
            {{ test_case.description }}
          </p>
          <div class="sentry-test-card__footer">
            <span class="sentry-test-card__expected">
              Sentry: <strong>FILTERED</strong>
            </span>
            <vb-button
              @click.native="run_test(test_case)"
              :vb_options="{
                text: test_case.running ? 'Running...' : 'Trigger',
                variant: 'outline-primary',
                size: 'sm',
                disabled: test_case.running,
              }"
            />
          </div>
          <div
            v-if="test_case.result"
            class="sentry-test-card__result"
            :class="`sentry-test-card__result--${test_case.result.status}`"
          >
            {{ test_case.result.message }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import * as Sentry from "@sentry/vue";
import { sentryEnabled } from "@src/sentry";

export default {
  name: "sentry-test",

  data() {
    return {
      sentry_enabled: sentryEnabled,
      app_mode: process.env.VUE_APP_MODE,
      dsn_masked: this.mask_dsn(process.env.VUE_APP_SENTRY_DSN),

      js_error_cases: [
        {
          id: "js_throw_error",
          title: "throw new Error()",
          type: "Error",
          severity: "error",
          description:
            "Throws a standard JS Error. Should be captured by Sentry global handler with full stack trace.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            throw new Error("[Sentry Test] Uncaught Error from library panel");
          },
        },
        {
          id: "js_type_error",
          title: "TypeError (undefined property)",
          type: "TypeError",
          severity: "error",
          description:
            "Accesses a property on undefined. Common runtime error that Sentry should capture.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            const obj = undefined;
            return obj.nonExistentProperty;
          },
        },
        {
          id: "js_reference_error",
          title: "ReferenceError (undefined variable)",
          type: "ReferenceError",
          severity: "error",
          description:
            "References an undeclared variable. Should produce a ReferenceError in Sentry.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            /* eslint-disable no-undef */
            return undeclaredVariableSentryTest;
            /* eslint-enable no-undef */
          },
        },
        {
          id: "js_unhandled_promise",
          title: "Unhandled Promise Rejection",
          type: "Promise",
          severity: "error",
          description:
            "Creates an unhandled promise rejection. Sentry should capture this via the unhandledrejection handler.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            Promise.reject(
              new Error("[Sentry Test] Unhandled Promise Rejection"),
            );
          },
        },
      ],

      sentry_api_cases: [
        {
          id: "sentry_capture_exception",
          title: "captureException()",
          type: "captureException",
          severity: "error",
          description:
            "Directly calls Sentry.captureException() with an Error object. The most explicit way to report.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            const event_id = Sentry.captureException(
              new Error("[Sentry Test] captureException from library panel"),
            );
            return { event_id };
          },
        },
        {
          id: "sentry_capture_message",
          title: "captureMessage()",
          type: "captureMessage",
          severity: "warning",
          description:
            "Calls Sentry.captureMessage() to send an informational message (not an error).",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            const event_id = Sentry.captureMessage(
              "[Sentry Test] captureMessage from library panel",
              "info",
            );
            return { event_id };
          },
        },
        {
          id: "sentry_capture_with_context",
          title: "captureException() with context",
          type: "captureException",
          severity: "error",
          description:
            "Reports an error with extra context (tags, extras) similar to what get_sentry_extras() adds.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            const event_id = Sentry.captureException(
              new Error("[Sentry Test] captureException with context"),
              {
                tags: { caller: "sentry-test-panel", request_id: "test-123" },
                extra: { status: 500, url: "/api/test" },
              },
            );
            return { event_id };
          },
        },
        {
          id: "sentry_set_context_breadcrumb",
          title: "addBreadcrumb() + captureException()",
          type: "Breadcrumb",
          severity: "info",
          description:
            "Adds a breadcrumb then triggers an error. Validates that breadcrumbs appear in Sentry event detail.",
          should_report: true,
          running: false,
          result: null,
          action: () => {
            Sentry.addBreadcrumb({
              category: "sentry-test",
              message: "User clicked test button in library panel",
              level: "info",
            });
            const event_id = Sentry.captureException(
              new Error("[Sentry Test] Error with breadcrumb trail"),
            );
            return { event_id };
          },
        },
      ],


      noise_filtering_cases: [
        {
          id: "noise_navigation_duplicated",
          title: "NavigationDuplicated",
          type: "Vue Router",
          severity: "filtered",
          description:
            'Navigates to the current route. Should be filtered by ignoreErrors "NavigationDuplicated".',
          should_report: false,
          running: false,
          result: null,
          action: (vm) => {
            vm.$router.push(vm.$route.fullPath).catch(() => {});
            return { filtered: true };
          },
        },
        {
          id: "noise_non_error_exception",
          title: "Non-Error thrown (string)",
          type: "Non-Error",
          severity: "filtered",
          description:
            'Throws a string instead of an Error object. Filtered by ignoreErrors "Non-Error exception captured".',
          should_report: false,
          running: false,
          result: null,
          action: () => {
            throw "This is a string, not an Error object"; // eslint-disable-line no-throw-literal
          },
        },
        {
          id: "noise_chunk_load_error",
          title: "ChunkLoadError",
          type: "ChunkLoadError",
          severity: "filtered",
          description:
            'Simulates a chunk loading error (stale cache after deploy). Filtered by ignoreErrors "ChunkLoadError".',
          should_report: false,
          running: false,
          result: null,
          action: () => {
            const error = new Error("Loading chunk 42 failed");
            error.name = "ChunkLoadError";
            throw error;
          },
        },
        {
          id: "noise_resize_observer",
          title: "ResizeObserver loop",
          type: "ResizeObserver",
          severity: "filtered",
          description:
            "Throws a ResizeObserver loop error. Filtered by ignoreErrors /ResizeObserver loop/.",
          should_report: false,
          running: false,
          result: null,
          action: () => {
            throw new Error(
              "ResizeObserver loop completed with undelivered notifications.",
            );
          },
        },
        {
          id: "noise_abort_error",
          title: "AbortError (cancelled request)",
          type: "AbortError",
          severity: "filtered",
          description:
            "Simulates a cancelled/aborted request. Filtered by should_report_to_sentry() and ignoreErrors.",
          should_report: false,
          running: false,
          result: null,
          action: () => {
            const error = new Error("The operation was aborted");
            error.name = "AbortError";
            throw error;
          },
        },
      ],
    };
  },

  methods: {
    mask_dsn(dsn) {
      if (!dsn) return "not configured";
      try {
        const url = new URL(dsn);
        const key = url.username;
        return `${key.substring(0, 6)}...@${url.hostname}`;
      } catch {
        return "invalid DSN";
      }
    },

    async run_test(test_case) {
      test_case.running = true;
      test_case.result = null;

      try {
        const result = await test_case.action(this);

        if (result && result.event_id) {
          test_case.result = {
            status: "ok",
            message: `Sent to Sentry (event_id: ${result.event_id})`,
          };
        } else if (result && result.filtered) {
          test_case.result = {
            status: "ok",
            message: "Error thrown — should be filtered by Sentry config",
          };
        } else if (result && result.status) {
          test_case.result = result;
        } else {
          test_case.result = {
            status: "ok",
            message: test_case.should_report
              ? "Error thrown — check Sentry dashboard for the event"
              : "Error thrown — should be filtered (not in Sentry)",
          };
        }
      } catch (error) {
        test_case.result = {
          status: test_case.should_report ? "ok" : "warning",
          message: test_case.should_report
            ? `Error thrown: ${error.message} — check Sentry dashboard`
            : `Error thrown: ${error.message} — should be filtered`,
        };
      } finally {
        test_case.running = false;
      }
    },
  },
};
</script>

<style lang="scss">
.sentry-test-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sentry-test-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  display: flex;
  flex-direction: column;

  &__header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  &__badge {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;

    &--error {
      background: #fee2e2;
      color: #991b1b;
    }
    &--warning {
      background: #fef3c7;
      color: #92400e;
    }
    &--info {
      background: #dbeafe;
      color: #1e40af;
    }
    &--filtered {
      background: #e5e7eb;
      color: #4b5563;
    }
  }

  &__type {
    font-size: 12px;
    color: #6b7280;
    font-family: monospace;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 4px;
    font-family: monospace;
  }

  &__description {
    font-size: 13px;
    color: #4b5563;
    flex: 1;
    margin-bottom: 12px;
    line-height: 1.4;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #f3f4f6;
    padding-top: 12px;
  }

  &__expected {
    font-size: 12px;
    color: #6b7280;
  }

  &__result {
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-family: monospace;
    word-break: break-all;

    &--ok {
      background: #ecfdf5;
      color: #065f46;
      border: 1px solid #a7f3d0;
    }
    &--error {
      background: #fef2f2;
      color: #991b1b;
      border: 1px solid #fecaca;
    }
    &--warning {
      background: #fffbeb;
      color: #92400e;
      border: 1px solid #fde68a;
    }
  }
}
</style>
